import type { Logout } from 'flowhawk-core';
import type { Reducer } from '@reduxjs/toolkit';

import type { ResetData } from './ApplicationActions';

export type ResetExpiredPasswordError = {
  nonFieldError?: string;
  error_id?: string;
  tokenExpired?: boolean;
};

export type PasswordPolicy = {
  minLength: number;
  minLowerCase: number;
  minUpperCase: number;
  minNumber: number;
  minSymbol: number;
  excludeUsername: boolean;
  excludeAttributes: string[];
  excludeCommon: boolean;
  historyCount: number;
};

export type ResetExpiredPassword = {
  type: 'PasswordReset/RESET_EXPIRED_PASSWORD';
  token: string;
  newPassword: string;
  confirmPassword: string;
};

export type ResetExpiredPasswordSuccess = {
  type: 'PasswordReset/RESET_EXPIRED_PASSWORD_SUCCESS';
};

export type ResetExpiredPasswordFailed = {
  type: 'PasswordReset/RESET_EXPIRED_PASSWORD_FAILED';
  error: ResetExpiredPasswordError;
};

export type GetPasswordPolicy = {
  type: 'PasswordReset/GET_PASSWORD_POLICY';
};

export type ReceivePasswordPolicy = {
  type: 'PasswordReset/RECEIVE_PASSWORD_POLICY';
  policy: PasswordPolicy;
};

export type GetPasswordPolicyFailed = {
  type: 'PasswordReset/GET_PASSWORD_POLICY_FAILED';
};

type Action =
  | ResetExpiredPassword
  | ResetExpiredPasswordSuccess
  | ResetExpiredPasswordFailed
  | GetPasswordPolicy
  | ReceivePasswordPolicy
  | GetPasswordPolicyFailed
  | ResetData
  | Logout;

export const resetExpiredPassword = (
  token: string,
  newPassword: string,
  confirmPassword: string,
): ResetExpiredPassword => ({
  type: 'PasswordReset/RESET_EXPIRED_PASSWORD',
  token,
  newPassword,
  confirmPassword,
});

export const resetExpiredPasswordSuccess = (): ResetExpiredPasswordSuccess => ({
  type: 'PasswordReset/RESET_EXPIRED_PASSWORD_SUCCESS',
});

export const resetExpiredPasswordFailed = (error: ResetExpiredPasswordError): ResetExpiredPasswordFailed => ({
  type: 'PasswordReset/RESET_EXPIRED_PASSWORD_FAILED',
  error,
});

export const getPasswordPolicy = (): GetPasswordPolicy => ({
  type: 'PasswordReset/GET_PASSWORD_POLICY',
});

export const receivePasswordPolicy = (policy: PasswordPolicy): ReceivePasswordPolicy => ({
  type: 'PasswordReset/RECEIVE_PASSWORD_POLICY',
  policy,
});

export const getPasswordPolicyFailed = (): GetPasswordPolicyFailed => ({
  type: 'PasswordReset/GET_PASSWORD_POLICY_FAILED',
});

export type PasswordResetState = {
  loading: boolean;
  success: boolean;
  error: ResetExpiredPasswordError | null;
  tokenExpired: boolean;
  passwordPolicy: PasswordPolicy | null;
  policyLoading: boolean;
};

const initialState: PasswordResetState = {
  loading: false,
  success: false,
  error: null,
  tokenExpired: false,
  passwordPolicy: null,
  policyLoading: false,
};

const reducer = (state: PasswordResetState = initialState, action: Action): PasswordResetState => {
  switch (action.type) {
    case 'Application/RESET_DATA':
    case 'Auth/LOGOUT':
      return {
        ...initialState,
      };

    case 'PasswordReset/RESET_EXPIRED_PASSWORD':
      return {
        ...state,
        loading: true,
        error: null,
        success: false,
        tokenExpired: false,
      };

    case 'PasswordReset/RESET_EXPIRED_PASSWORD_SUCCESS':
      return {
        ...state,
        loading: false,
        success: true,
        error: null,
      };

    case 'PasswordReset/RESET_EXPIRED_PASSWORD_FAILED':
      return {
        ...state,
        loading: false,
        success: false,
        error: action.error,
        tokenExpired: action.error.error_id === 'Auth.ResetExpiredPassword.TokenExpired',
      };

    case 'PasswordReset/GET_PASSWORD_POLICY':
      return {
        ...state,
        policyLoading: true,
      };

    case 'PasswordReset/RECEIVE_PASSWORD_POLICY':
      return {
        ...state,
        passwordPolicy: action.policy,
        policyLoading: false,
      };

    case 'PasswordReset/GET_PASSWORD_POLICY_FAILED':
      // On failure, passwordPolicy remains null in Redux state.
      // All password validation forms use defaultPasswordPolicy as fallback.
      return {
        ...state,
        policyLoading: false,
      };

    default:
      return state;
  }
};

export default reducer as Reducer<PasswordResetState>;
